import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';

const STATUS_LABELS = {
  abierta: 'Abierta',
  en_reparacion: 'En reparación',
  resuelta: 'Resuelta',
};

const STATUS_BADGE = {
  abierta: 'badge-yellow',
  en_reparacion: 'badge-blue',
  resuelta: 'badge-green',
};

const IconPencil = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23',
    });
  } catch { return iso; }
}

export default function ItemDetailModal({ item: initialItem, onClose, onEdit, unitCount }) {
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  // Report incident
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [incidentForm, setIncidentForm] = useState({ description: '', can_operate: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);

  // Edit incident
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ description: '', can_operate: true });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete incident
  const [deletingId, setDeletingId] = useState(null);

  const canOperate = canWrite(user, 'inventario.incidencias');
  const isAdmin = canWrite(user, 'inventario.stock');

  const loadItem = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inventory/items/${initialItem.id}`);
      setItem(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItem(); }, [initialItem.id]);

  const handleReportIncident = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/inventory/incidents', {
        item_id: item.id,
        description: incidentForm.description,
        can_operate: incidentForm.can_operate,
      });
      setShowIncidentForm(false);
      setIncidentForm({ description: '', can_operate: true });
      setDirty(true);
      await loadItem();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al reportar');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (incidentId, status) => {
    try {
      await api.put(`/inventory/incidents/${incidentId}`, { status });
      setDirty(true);
      await loadItem();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleEditStart = (inc) => {
    setEditingId(inc.id);
    setEditForm({ description: inc.description, can_operate: inc.can_operate });
    setEditError('');
    setDeletingId(null);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditError('');
  };

  const handleEditSave = async (e, incidentId) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/inventory/incidents/${incidentId}`, {
        description: editForm.description,
        can_operate: editForm.can_operate,
      });
      setEditingId(null);
      setDirty(true);
      await loadItem();
    } catch (e) {
      setEditError(e.response?.data?.error || 'Error al guardar');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteConfirm = async (incidentId) => {
    try {
      await api.delete(`/inventory/incidents/${incidentId}`);
      setDeletingId(null);
      setDirty(true);
      await loadItem();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleClose = () => onClose(dirty);

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal"
        style={{ maxWidth: 560 }}
        onClick={e => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex items-center gap-8 text-dim">
            <div className="spinner" style={{ width: 20, height: 20 }} />
            Cargando...
          </div>
        ) : !item ? (
          <div className="text-dim text-sm">No se pudo cargar el ítem.</div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 12 }}>
              <div>
                <div className="modal-title" style={{ marginBottom: 6 }}>{item.name}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className="text-dim text-sm">{item.category}</span>
                  <span className="text-mute text-sm">·</span>
                  <span className="text-dim text-sm">{item.subcategory}</span>
                </div>
              </div>
              <button className="btn btn-icon btn-ghost" onClick={handleClose}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Meta */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
              <div>
                <div className="form-label" style={{ marginBottom: 4 }}>Cantidad</div>
                <div style={{ fontWeight: 600 }}>×{unitCount ?? item.quantity}</div>
              </div>
              {item.pack_name && (
                <div>
                  <div className="form-label" style={{ marginBottom: 4 }}>Pack</div>
                  <div style={{ fontWeight: 600 }}>{item.pack_name}</div>
                </div>
              )}
              <div>
                <div className="form-label" style={{ marginBottom: 4 }}>Estado</div>
                {item.has_open_incident
                  ? <span className="badge badge-yellow">⚠ Incidencia</span>
                  : item.is_operational === false
                    ? <span className="badge badge-red">✕ No operativo</span>
                    : <span className="badge badge-green">Operativo</span>
                }
              </div>
            </div>

            {item.notes && (
              <div style={{ marginBottom: 20 }}>
                <div className="form-label" style={{ marginBottom: 4 }}>Notas</div>
                <div className="text-sm text-dim" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {item.notes}
                </div>
              </div>
            )}

            <div className="divider" />

            {/* Incidents header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                Incidencias{item.incidents?.length > 0 ? ` (${item.incidents.length})` : ''}
              </div>
              {canOperate && !showIncidentForm && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowIncidentForm(true)}
                >
                  + Reportar
                </button>
              )}
            </div>

            {/* Report form */}
            {showIncidentForm && (
              <form onSubmit={handleReportIncident} className="inv-incident-form">
                {error && <div className="inv-error">{error}</div>}
                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <textarea
                    className="input"
                    rows={3}
                    value={incidentForm.description}
                    onChange={e => setIncidentForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="¿Qué pasó con el equipo?"
                    required
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <label className="inv-checkbox-label">
                  <input
                    type="checkbox"
                    checked={incidentForm.can_operate}
                    onChange={e => setIncidentForm(f => ({ ...f, can_operate: e.target.checked }))}
                  />
                  Puede seguir funcionando
                </label>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => { setShowIncidentForm(false); setError(''); }}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            )}

            {/* Incidents list */}
            {!item.incidents?.length ? (
              <div className="text-dim text-sm">Sin incidencias registradas.</div>
            ) : (
              <div className="inv-incidents-list">
                {item.incidents.map(inc => (
                  <div key={inc.id} className="inv-incident-card">
                    <div className="inv-incident-header">
                      <span className={`badge ${STATUS_BADGE[inc.status] || 'badge-gray'}`}>
                        {STATUS_LABELS[inc.status] || inc.status}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {canOperate && (
                          <select
                            className="input"
                            style={{ width: 'auto', padding: '3px 8px', fontSize: 12 }}
                            value={inc.status}
                            onChange={e => handleStatusChange(inc.id, e.target.value)}
                          >
                            <option value="abierta">Abierta</option>
                            <option value="en_reparacion">En reparación</option>
                            <option value="resuelta">Resuelta</option>
                          </select>
                        )}
                        {canOperate && editingId !== inc.id && (
                          <button
                            className="btn btn-icon btn-ghost"
                            onClick={() => handleEditStart(inc)}
                            title="Editar incidencia"
                          >
                            <IconPencil />
                          </button>
                        )}
                        {isAdmin && deletingId !== inc.id && (
                          <button
                            className="btn btn-icon btn-ghost inv-delete-btn"
                            onClick={() => { setDeletingId(inc.id); setEditingId(null); }}
                            title="Eliminar incidencia"
                          >
                            <IconTrash />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Edit form */}
                    {editingId === inc.id ? (
                      <form onSubmit={e => handleEditSave(e, inc.id)} className="inv-incident-form" style={{ margin: '0 0 8px' }}>
                        {editError && <div className="inv-error">{editError}</div>}
                        <div className="form-group">
                          <textarea
                            className="input"
                            rows={3}
                            value={editForm.description}
                            onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                            required
                            style={{ resize: 'vertical' }}
                          />
                        </div>
                        <label className="inv-checkbox-label">
                          <input
                            type="checkbox"
                            checked={editForm.can_operate}
                            onChange={e => setEditForm(f => ({ ...f, can_operate: e.target.checked }))}
                          />
                          Puede seguir funcionando
                        </label>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={handleEditCancel}>
                            Cancelar
                          </button>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={editSaving}>
                            {editSaving ? 'Guardando...' : 'Guardar'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div className="text-sm" style={{ marginBottom: 8, lineHeight: 1.4 }}>
                          {inc.description}
                        </div>
                        <div className="text-xs text-dim" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                          {!inc.can_operate && (
                            <span className="badge badge-red" style={{ fontSize: 10 }}>No operable</span>
                          )}
                          <span>
                            Reportado por <strong>{inc.reported_by_name}</strong> · {fmtDate(inc.reported_at)}
                          </span>
                          {inc.resolved_by_name && (
                            <span>
                              · Resuelto por <strong>{inc.resolved_by_name}</strong> · {fmtDate(inc.resolved_at)}
                            </span>
                          )}
                        </div>
                      </>
                    )}

                    {/* Delete confirm */}
                    {deletingId === inc.id && (
                      <div className="inv-delete-confirm">
                        <span className="text-sm">¿Eliminar esta incidencia?</span>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setDeletingId(null)}>
                            Cancelar
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ background: 'var(--red)', color: '#fff', border: 'none' }}
                            onClick={() => handleDeleteConfirm(inc.id)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Edit item button (admin only) */}
            {isAdmin && onEdit && (
              <>
                <div className="divider" />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => onEdit(item)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconPencil /> Editar ítem
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
